import {
  List,
  Datagrid,
  TextField,
  DateField,
  BooleanField,
  Edit,
  Create,
  SimpleForm,
  TextInput,
  DateTimeInput,
  BooleanInput,
  NumberInput,
  ReferenceInput,
  SelectInput,
  ReferenceArrayInput,
  SelectArrayInput,
  useRecordContext,
  useNotify,
  SearchInput,
  FunctionField,
} from "react-admin";

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";

import { useFormContext } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";

const POSITIONS = [
  "Portiere",
  "Terzino destro",
  "Difensore Centrale",
  "Terzino sinistro",
  "Mezzala destra",
  "Mezzala sinistra",
  "Mediano",
  "Ala destra",
  "Punta",
  "Ala sinistra",
];

const STATUS_CHOICES = [
  { id: "scheduled", name: "In programma" },
  { id: "played", name: "Giocata" },
  { id: "canceled", name: "Annullata" },
];

const COMP_CHOICES = [
  { id: "league", name: "Campionato" },
  { id: "cup", name: "Coppa" },
  { id: "friendly", name: "Amichevole" },
];

const matchFilters = [
  <SearchInput key="q" source="q" alwaysOn placeholder="Cerca..." />,
  <ReferenceInput key="season_id" source="season_id" reference="admin/seasons" alwaysOn>
    <SelectInput label="Stagione" optionText="label" emptyText="Tutte le stagioni" />
  </ReferenceInput>,
  <SelectInput
    key="status"
    source="status"
    label="Stato"
    choices={STATUS_CHOICES}
    emptyText="Tutti"
  />,
  <SelectInput
    key="competition"
    source="competition"
    label="Competizione"
    choices={COMP_CHOICES}
    emptyText="Tutte"
  />,
];

const StatusChip = ({ record }) => {
  const status = record?.status;
  if (!status) return null;

  const label =
    STATUS_CHOICES.find((s) => s.id === status)?.name ?? status;

  const variant = status === "played" ? "filled" : "outlined";
  return <Chip size="small" label={label} variant={variant} />;
};

const CompChip = ({ record }) => {
  const c = record?.competition;
  if (!c) return null;
  const label = COMP_CHOICES.find((x) => x.id === c)?.name ?? c;
  return <Chip size="small" label={label} variant="outlined" />;
};

const HomeAwayChip = ({ record }) => {
  const isHome = !!record?.is_home;
  return (
    <Chip
      size="small"
      label={isHome ? "Casa" : "Trasferta"}
      variant="outlined"
    />
  );
};

export const MatchList = () => (
  <List
    filters={matchFilters}
    perPage={25}
    sort={{ field: "starts_at", order: "DESC" }}
    sx={{ "& .RaList-content": { marginTop: 1 } }}
  >
    <Datagrid
      rowClick="edit"
      bulkActionButtons={false}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        "& .RaDatagrid-headerCell": { fontWeight: 800 },
        "& .RaDatagrid-rowCell": { py: 1.25 },
      }}
    >
      <TextField source="id" label="ID" />
      <DateField source="starts_at" label="Data" showTime />
      <TextField source="opponent.name" label="Avversario" />
      <FunctionField label="Casa/Trasf." render={(record) => <HomeAwayChip record={record} />} />
      <FunctionField label="Stato" render={(record) => <StatusChip record={record} />} />
      <FunctionField label="Competizione" render={(record) => <CompChip record={record} />} />
    </Datagrid>
  </List>
);

const MatchHeaderCard = () => {
  const record = useRecordContext();
  const opponent = record?.opponent?.name || "—";
  const when = record?.starts_at
    ? new Date(record.starts_at).toLocaleString("it-IT")
    : "—";

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
        mb: 2,
      }}
    >
      <CardContent>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
          <Box>
            <Typography variant="h6" fontWeight={900}>
              Partita #{record?.id ?? "—"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {when} · {record?.field_name || "Campo non impostato"} · {record?.is_home ? "Casa" : "Trasferta"}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={opponent} variant="outlined" />
            {record?.status ? <StatusChip record={record} /> : null}
            {record?.competition ? <CompChip record={record} /> : null}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const MatchBaseFields = () => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 4,
      border: "1px solid",
      borderColor: "divider",
      mb: 2,
    }}
  >
    <CardContent>
      <Typography fontWeight={900} sx={{ mb: 1 }}>
        Dettagli partita
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        <DateTimeInput source="starts_at" label="Data e ora" />

        <TextInput source="field_name" label="Campo" />

        <BooleanInput source="is_home" label="In casa" />

        <ReferenceInput source="opponent_id" reference="admin/opponents" label="Avversario">
          <SelectInput optionText="name" />
        </ReferenceInput>

        <SelectInput
          source="status"
          label="Stato partita"
          choices={STATUS_CHOICES}
        />

        <SelectInput
          source="competition"
          label="Competizione"
          choices={COMP_CHOICES}
          defaultValue="league"
        />

        <NumberInput source="home_goals" label="Gol Casa" />
        <NumberInput source="away_goals" label="Gol Trasferta" />

        <TextInput source="notes" label="Note" multiline fullWidth />
      </Box>
    </CardContent>
  </Card>
);

export const MatchEdit = () => (
  <Edit title="Modifica partita">
    <SimpleForm>
      <MatchHeaderCard />
      <MatchBaseFields />

      <Accordion defaultExpanded sx={{ borderRadius: 4, overflow: "hidden", mb: 2, border: "1px solid", borderColor: "divider" }} elevation={0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={900}>Convocati</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CallupsForm />
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ borderRadius: 4, overflow: "hidden", mb: 2, border: "1px solid", borderColor: "divider" }} elevation={0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={900}>Formazione</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <LineupForm />
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ borderRadius: 4, overflow: "hidden", border: "1px solid", borderColor: "divider" }} elevation={0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={900}>Eventi partita</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <EventsForm />
        </AccordionDetails>
      </Accordion>
    </SimpleForm>
  </Edit>
);

export const MatchCreate = () => (
  <Create title="Nuova partita">
    <SimpleForm>
      <MatchBaseFields />
    </SimpleForm>
  </Create>
);

/* -------------------- Convocati -------------------- */

const CallupsForm = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const { setValue, getValues } = useFormContext();

  useEffect(() => {
    if (!record) return;

    fetch(`/api/admin/matches/${record.id}/callups`)
      .then((res) => res.json())
      .then((data) => setValue("callups", data, { shouldDirty: false }))
      .catch(() => notify("Errore caricamento convocati", { type: "error" }));
  }, [record, notify, setValue]);
  

  if (!record) return null;

  const saveCallups = async () => {
    const ids = getValues("callups") || [];
    try {
      await fetch(`/api/admin/matches/${record.id}/callups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ player_ids: ids }),
      });
      notify("Convocazioni salvate", { type: "info" });
    } catch {
      notify("Errore nel salvataggio", { type: "error" });
    }
  };

  return (
    <Stack spacing={2}>
      <ReferenceArrayInput source="callups" reference="admin/players" label="Convocati">
        <SelectArrayInput optionText="name" fullWidth />
      </ReferenceArrayInput>

      <Stack direction="row" justifyContent="flex-end">
        <Button startIcon={<SaveRoundedIcon />} variant="contained" onClick={saveCallups}>
          Salva convocati
        </Button>
      </Stack>
    </Stack>
  );
};

/* -------------------- Formazione -------------------- */

const LineupForm = () => {
  const record = useRecordContext();
  const notify = useNotify();

  const [lineup, setLineup] = useState([]);
  const [players, setPlayers] = useState([]);

  useEffect(() => {
    fetch("/api/admin/players")
      .then((res) => res.json())
      .then((data) => setPlayers(data))
      .catch(() => notify("Errore caricamento giocatori", { type: "error" }));
  }, [notify]);

  useEffect(() => {
    if (!record) return;

    fetch(`/api/admin/matches/${record.id}/lineup`)
      .then((res) => res.json())
      .then((data) => setLineup(data))
      .catch(() => notify("Errore caricamento formazione", { type: "error" }));
  }, [record, notify]);

  if (!record) return null;

  const updateLine = (index, field, value) => {
    setLineup((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const addPlayer = () => {
    setLineup((prev) => [...prev, { player_id: null, position: "", is_starting: true }]);
  };

  const removeLine = (index) => {
    setLineup((prev) => prev.filter((_, i) => i !== index));
  };

  const saveLineup = async () => {
    const cleaned = lineup.filter((x) => x.player_id);
    try {
      await fetch(`/api/admin/matches/${record.id}/lineup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cleaned),
      });
      notify("Formazione salvata", { type: "info" });
    } catch {
      notify("Errore salvataggio formazione", { type: "error" });
    }
  };

  return (
    <Stack spacing={2}>
      {lineup.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nessun giocatore inserito.
        </Typography>
      ) : null}

      <Stack spacing={1.5}>
        {lineup.map((row, index) => (
          <Card
            key={index}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "2fr 2fr 1fr auto" },
                  gap: 1.5,
                  alignItems: "center",
                }}
              >
                <MuiTextField
                  select
                  label="Giocatore"
                  value={row.player_id ?? ""}
                  onChange={(e) =>
                    updateLine(index, "player_id", e.target.value ? Number(e.target.value) : null)
                  }
                  size="small"
                  fullWidth
                >
                  <MenuItem value="">Seleziona</MenuItem>
                  {players.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </MuiTextField>

                <MuiTextField
                  select
                  label="Posizione"
                  value={row.position ?? ""}
                  onChange={(e) => updateLine(index, "position", e.target.value)}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="">Seleziona</MenuItem>
                  {POSITIONS.map((pos) => (
                    <MenuItem key={pos} value={pos}>
                      {pos}
                    </MenuItem>
                  ))}
                </MuiTextField>

                <MuiTextField
                  select
                  label="Titolare"
                  value={row.is_starting ? "yes" : "no"}
                  onChange={(e) => updateLine(index, "is_starting", e.target.value === "yes")}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="yes">Sì</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </MuiTextField>

                <IconButton onClick={() => removeLine(index)} title="Rimuovi">
                  <DeleteOutlineRoundedIcon />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Divider />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
        <Button startIcon={<AddRoundedIcon />} variant="outlined" onClick={addPlayer}>
          Aggiungi giocatore
        </Button>

        <Button startIcon={<SaveRoundedIcon />} variant="contained" onClick={saveLineup}>
          Salva formazione
        </Button>
      </Stack>
    </Stack>
  );
};

/* -------------------- Eventi -------------------- */

const EVENT_TYPES = [
  { id: "goal", name: "Gol" },
  { id: "own_goal", name: "Autogol" },
  { id: "yellow", name: "Giallo" },
  { id: "red", name: "Rosso" },
  { id: "substitution", name: "Sostituzione" },
];

const EventsForm = () => {
  const record = useRecordContext();
  const notify = useNotify();

  const [events, setEvents] = useState([]);
  const [players, setPlayers] = useState([]);

  useEffect(() => {
    fetch("/api/admin/players")
      .then((res) => res.json())
      .then((data) => setPlayers(data))
      .catch(() => notify("Errore caricamento giocatori", { type: "error" }));
  }, [notify]);

  useEffect(() => {
    if (!record) return;

    fetch(`/api/admin/matches/${record.id}/events`)
      .then((res) => res.json())
      .then((data) => setEvents(data))
      .catch(() => notify("Errore caricamento eventi", { type: "error" }));
  }, [record, notify]);

  const playerOptions = useMemo(
    () => players.map((p) => ({ id: p.id, name: p.name })),
    [players]
  );

  if (!record) return null;

  const addEmptyEvent = () => {
    setEvents((prev) => [
      ...prev,
      {
        id: null,
        minute: 0,
        type: "goal",
        player_id: null,
        assist_player_id: null,
        player_in_id: null,
        player_out_id: null,
        is_opponent: false,
        note: "",
      },
    ]);
  };

  const updateEvent = (index, field, value) => {
    setEvents((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const removeLocalRow = (index) => {
    setEvents((prev) => prev.filter((_, i) => i !== index));
  };

  const deleteSavedEvent = async (eventId) => {
    try {
      await fetch(`/api/admin/matches/${record.id}/events/${eventId}`, {
        method: "DELETE",
      });
      setEvents((prev) => prev.filter((e) => e.id !== eventId));
      notify("Evento eliminato", { type: "info" });
    } catch {
      notify("Errore eliminazione evento", { type: "error" });
    }
  };

  const saveAllEvents = async () => {
    try {
      for (const ev of events) {
        const payload = {
          minute: Number(ev.minute),
          type: ev.type,
          player_id: ev.player_id ? Number(ev.player_id) : null,
          assist_player_id: ev.assist_player_id ? Number(ev.assist_player_id) : null,
          player_in_id: ev.player_in_id ? Number(ev.player_in_id) : null,
          player_out_id: ev.player_out_id ? Number(ev.player_out_id) : null,
          is_opponent: !!ev.is_opponent,
          note: ev.note || null,
        };

        if (ev.id) {
          await fetch(`/api/admin/matches/${record.id}/events/${ev.id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
        } else {
          await fetch(`/api/admin/matches/${record.id}/events`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
        }
      }

      const refreshed = await fetch(`/api/admin/matches/${record.id}/events`).then((r) =>
        r.json()
      );
      setEvents(refreshed);
      notify("Eventi salvati", { type: "info" });
    } catch {
      notify("Errore salvataggio eventi", { type: "error" });
    }
  };

  return (
    <Stack spacing={2}>
      {events.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nessun evento inserito.
        </Typography>
      ) : null}

      <Stack spacing={1.5}>
        {events.map((ev, index) => {
          const isSub = ev.type === "substitution";
          const isGoal = ev.type === "goal" || ev.type === "own_goal";

          return (
            <Card
              key={ev.id ?? `new-${index}`}
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "90px 200px 1fr auto" },
                    gap: 1.5,
                    alignItems: "center",
                  }}
                >
                  <MuiTextField
                    label="Min"
                    type="number"
                    value={ev.minute ?? 0}
                    onChange={(e) => updateEvent(index, "minute", e.target.value)}
                    size="small"
                    inputProps={{ min: 0, max: 130 }}
                  />

                  <MuiTextField
                    select
                    label="Tipo"
                    value={ev.type}
                    onChange={(e) => {
                      const t = e.target.value;
                      setEvents((prev) => {
                        const copy = [...prev];
                        const current = copy[index] || {};
                        const next = { ...current, type: t };

                        if (t === "substitution") {
                          next.player_id = null;
                          next.is_opponent = false;
                          next.assist_player_id = null;
                        } else {
                          next.player_in_id = null;
                          next.player_out_id = null;
                          if (t !== "goal") next.assist_player_id = null;
                        }

                        copy[index] = next;
                        return copy;
                      });
                    }}
                    size="small"
                    fullWidth
                  >
                    {EVENT_TYPES.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </MuiTextField>

                  {/* blocco dinamico */}
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.5}
                    alignItems={{ md: "center" }}
                  >
                    {isSub ? (
                      <>
                        <MuiTextField
                          select
                          label="Esce"
                          value={ev.player_out_id ?? ""}
                          onChange={(e) =>
                            updateEvent(
                              index,
                              "player_out_id",
                              e.target.value ? Number(e.target.value) : null
                            )
                          }
                          size="small"
                          sx={{ minWidth: 180 }}
                        >
                          <MenuItem value="">—</MenuItem>
                          {playerOptions.map((p) => (
                            <MenuItem key={p.id} value={p.id}>
                              {p.name}
                            </MenuItem>
                          ))}
                        </MuiTextField>

                        <MuiTextField
                          select
                          label="Entra"
                          value={ev.player_in_id ?? ""}
                          onChange={(e) =>
                            updateEvent(
                              index,
                              "player_in_id",
                              e.target.value ? Number(e.target.value) : null
                            )
                          }
                          size="small"
                          sx={{ minWidth: 180 }}
                        >
                          <MenuItem value="">—</MenuItem>
                          {playerOptions.map((p) => (
                            <MenuItem key={p.id} value={p.id}>
                              {p.name}
                            </MenuItem>
                          ))}
                        </MuiTextField>
                      </>
                    ) : (
                      <>
                        {!ev.is_opponent && (
                          <>
                            <MuiTextField
                              select
                              label="Giocatore"
                              value={ev.player_id ?? ""}
                              onChange={(e) =>
                                updateEvent(
                                  index,
                                  "player_id",
                                  e.target.value ? Number(e.target.value) : null
                                )
                              }
                              size="small"
                              sx={{ minWidth: 200 }}
                            >
                              <MenuItem value="">—</MenuItem>
                              {playerOptions.map((p) => (
                                <MenuItem key={p.id} value={p.id}>
                                  {p.name}
                                </MenuItem>
                              ))}
                            </MuiTextField>

                            {ev.type === "goal" && (
                              <MuiTextField
                                select
                                label="Assist"
                                value={ev.assist_player_id ?? ""}
                                onChange={(e) =>
                                  updateEvent(
                                    index,
                                    "assist_player_id",
                                    e.target.value ? Number(e.target.value) : null
                                  )
                                }
                                size="small"
                                sx={{ minWidth: 200 }}
                              >
                                <MenuItem value="">—</MenuItem>
                                {playerOptions
                                  .filter((p) => !ev.player_id || p.id !== ev.player_id)
                                  .map((p) => (
                                    <MenuItem key={p.id} value={p.id}>
                                      {p.name}
                                    </MenuItem>
                                  ))}
                              </MuiTextField>
                            )}
                          </>
                        )}

                        {isGoal && (
                          <MuiTextField
                            select
                            label="Gol avversario"
                            value={ev.is_opponent ? "yes" : "no"}
                            onChange={(e) => {
                              const checked = e.target.value === "yes";
                              setEvents((prev) => {
                                const copy = [...prev];
                                const current = copy[index] || {};
                                copy[index] = {
                                  ...current,
                                  is_opponent: checked,
                                  player_id: checked ? null : current.player_id ?? null,
                                  assist_player_id: checked ? null : current.assist_player_id ?? null,
                                };
                                return copy;
                              });
                            }}
                            size="small"
                            sx={{ minWidth: 160 }}
                          >
                            <MenuItem value="no">No</MenuItem>
                            <MenuItem value="yes">Sì</MenuItem>
                          </MuiTextField>
                        )}
                      </>
                    )}

                    <MuiTextField
                      label="Note"
                      value={ev.note || ""}
                      onChange={(e) => updateEvent(index, "note", e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </Stack>

                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    {ev.id ? (
                      <IconButton onClick={() => deleteSavedEvent(ev.id)} title="Elimina">
                        <DeleteOutlineRoundedIcon />
                      </IconButton>
                    ) : (
                      <IconButton onClick={() => removeLocalRow(index)} title="Rimuovi riga">
                        <DeleteOutlineRoundedIcon />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      <Divider />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
        <Button startIcon={<AddRoundedIcon />} variant="outlined" onClick={addEmptyEvent}>
          Aggiungi evento
        </Button>

        <Button startIcon={<SaveRoundedIcon />} variant="contained" onClick={saveAllEvents}>
          Salva eventi
        </Button>
      </Stack>
    </Stack>
  );
};
